import React from "react";

import { get, some, every, isEmpty } from "lodash";
import { connect, type ConnectedProps } from "react-redux";
import { push } from "store/actions/router";
import { userConstants } from "actions/types";
import { Row } from "react-bootstrap";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { createSupportRequest } from "actions/userActions";
import { isProcessingRolloverSelector } from "store/selectors/user";
import { createLoadingSelector, createErrorSelector } from "store/selectors";
import type { RootState } from "types/state";

import IconHeader from "components/IconHeader";
import ZendeskSupportRequest from "components/ZendeskSupportRequest";
import IndividualRolloverAccountType from "../../../signUp/individual/IndividualRolloverAccountType";
import IndividualCancelRollover from "../../../signUp/individual/IndividualCancelRollover";
import DistributionWorkflow from "./distributions/DistributionWorkflow";
import SimpleAccordion from "../../../../components/SimpleAccordion";

const actions = [userConstants.CREATE_REQUEST];

const loadingSelector = createLoadingSelector(actions);
// PRESERVED BUG (conversion-only): double-wrapped array (the documented
// createErrorSelector footgun). It still resolves at runtime — the one-element
// inner array stringifies back to "CREATE_REQUEST" in the `error.${action}`
// lookup — so behavior is unchanged; kept verbatim behind @ts-expect-error.
// @ts-expect-error nested string[][] vs the string | string[] signature
const errorSelector = createErrorSelector([actions]);

const mapStateToProps = (state: RootState) => {
  // get(..., {}) widens the result to `profile | {}`; cast to the fields read
  // here (firstName/lastName are String()-coerced below; preserves the original
  // undefined-safe destructure).
  const { firstName, lastName, email } = get(state, "user.profile", {}) as {
    firstName?: string;
    lastName?: string;
    email?: string;
  };

  const accounts = state.accounts.iraAccounts;

  // userState slice is typed `object` in RootState (deliberately shallow);
  // cast at the boundary to read the established `.state` field.
  const userState = (state.user.userState as { state: string }).state;
  const featureFlags = get(state.user, "featureFlags", []);
  const rolloverSupportFlag = some(featureFlags, {
    key: "RolloverSupport",
    value: "true",
  });

  const shouldNotDisplayDistributionSupport = accounts.every(
    (account) => get(account, "balances.totalAccountBalance", 0) === 0
  );

  return {
    email,
    name: `${String(firstName)} ${String(lastName)}`,
    userState,
    isProcessingRollover: isProcessingRolloverSelector(state),
    error: errorSelector(state),
    isSubmitting: loadingSelector(state),
    rolloverSupportFlag,
    accounts,
    shouldNotDisplayDistributionSupport,
  };
};

const mapDispatchToProps = {
  push,
  createSupportRequest,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = WithApolloClient<ConnectedProps<typeof connector>>;

interface State {
  showRolloverAccountTypeSelection: boolean;
  showCloseRolloverAccount: boolean;
  showDisbursementFlow: boolean;
  showSupportTicket: boolean;
}

interface SupportItem {
  index: number;
  disable?: boolean;
  value?: string;
  title: string;
  content: string;
  actionText: string;
  onClick: () => void;
}

class SupportPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      showRolloverAccountTypeSelection: false,
      showCloseRolloverAccount: false,
      showDisbursementFlow: false,
      showSupportTicket: false,
    };
  }

  _supportPage = () => {
    var articleStyle = {
      paddingBottom: 0,
    };

    const cancelRollover =
      !this.props.isProcessingRollover || !this.props.rolloverSupportFlag;

    const items: SupportItem[] = [
      {
        index: 1,
        disable: !this.props.rolloverSupportFlag,
        title: "Thinking About Rolling Over an Account?",
        content:
          "Start your account rollover today to consolidate your savings and take the next step toward achieving your financial goals.",
        actionText: "Start Rollover",
        onClick: () => {
          this.setState({
            showRolloverAccountTypeSelection: true,
          });
        },
      },

      {
        index: 2,
        disable: cancelRollover,
        title: "Need to Cancel Your Rollover Request?",
        content:
          "Follow these simple steps to quickly stop your rollover process.",
        actionText: "Cancel Rollover",
        onClick: () => {
          this.setState({
            showCloseRolloverAccount: true,
          });
        },
      },
      {
        index: 3,
        title: "Need to Update Your Address?",
        content:
          "Easily update your address with these simple steps. Make sure to have your driver’s license or state-issued ID handy.",
        actionText: "Update Address",
        onClick: () => {
          this.props.push("/dashboard/profile/info");
        },
      },
      {
        index: 4,
        disable: this.props.shouldNotDisplayDistributionSupport,
        value: "withdrawal",
        title: "Looking to Access Your Funds?",
        content: "Follow these simple steps to easily access your funds.",
        actionText: "Withdraw Funds",
        onClick: () => {
          this.setState({
            showDisbursementFlow: true,
          });
        },
      },
    ];

    const allItemsHidden = every(items, { disable: true });
    if (isEmpty(items) || allItemsHidden) {
      return null;
    }

    const filteredSupportItems = items.filter((item) => !item.disable);

    return (
      <div>
        <section>
          <article style={articleStyle}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <IconHeader
                variant="pageHeader"
                headerText="How can we help you?"
              />

              <Row
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "stretch",
                  paddingTop: 30,
                  width: "100%",
                }}
              >
                <div style={{ width: "100%" }}>
                  {filteredSupportItems.map((item) => (
                    <SimpleAccordion
                      // @ts-expect-error SimpleAccordion declares no `style` prop; this is an inert pass-through the component never reads (preserved verbatim).
                      style={{ fontColor: "#009BAA" }}
                      defaultStyle={false}
                      key={item.value}
                      title={item.title}
                      body={item.content}
                      onClick={item.onClick}
                      actionText={item.actionText}
                    />
                  ))}
                </div>
              </Row>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                paddingTop: 50,
              }}
            >
              <p style={{ color: "#0a2540", fontSize: 14 }}>
                Can&apos;t find what your are looking for?{" "}
                <a
                  onClick={() => {
                    this.setState({
                      showSupportTicket: true,
                    });
                  }}
                  style={{ color: "#009baa", cursor: "pointer" }}
                >
                  Tell us what you need, and we’ll be happy to assist!
                </a>
              </p>
            </div>
          </article>
        </section>
      </div>
    );
  };

  render() {
    let content;

    if (this.state.showRolloverAccountTypeSelection) {
      content = <IndividualRolloverAccountType />;
    } else if (this.state.showCloseRolloverAccount) {
      content = <IndividualCancelRollover />;
    } else if (this.state.showDisbursementFlow) {
      content = <DistributionWorkflow />;
    } else if (this.state.showSupportTicket) {
      content = <ZendeskSupportRequest />;
    } else {
      content = this._supportPage();
    }

    return <>{content}</>;
  }
}

export default connector(withApollo<Props>(SupportPage));
